import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { usePrevious } from 'react-use';
import { refreshToken } from '@store/auth/auth.actions';
import {
  actions as basketEditOrderActions,
  selectors as basketEditOrderSelectors,
} from '@store/basket/basketEditOrder.slice';
import {
  actions as basketNewOrderActions,
  selectors as basketNewOrderSelectors,
} from '@store/basket/basketNewOrder.slice';
import { selectDisableTracking } from '@store/nativeAppConfig/nativeAppConfig.slice';
import {
  useIsFetching,
  useIsMutating,
  useQueryClient,
} from '@tanstack/react-query';
import first from 'lodash/first';
import isEmpty from 'lodash/isEmpty';
import pickBy from 'lodash/pickBy';
import some from 'lodash/some';
import { getBasketPrices } from '@components/modules/Basket/BasketStatic/useBasketPrices';
import BASKET_MODES from '@constants/basketModes';
import BASKET_PAYMENT_MODES from '@constants/basketPaymentModes';
import BASKET_ROWS_TYPES from '@constants/basketRowsTypes';
import DIET_LENGTH_MODE from '@constants/dietLengtMode';
import ERROR_TYPES from '@constants/errorTypes';
import { NOTIFICATION_KEYS } from '@constants/notificationKeys';
import ROUTE_URLS from '@constants/routeUrls';
import TAB_SLUGS from '@constants/tabSlugs';
import {
  changeStepAndResetValid,
  selectOrderTabsPick,
  selectTabs,
} from '@features/orderTabs/orderTabsSlice';
import useDefaultDietElement from '@hooks/basket/useDefaultDietElement';
import useStaticBasketStore from '@hooks/basket/useStaticBasketStore';
import useTransformResponseBasketRedux from '@hooks/basket/useTransformResponseBasketRedux';
import useValidateBasket from '@hooks/basket/useValidateBasket';
import {
  selectBranding,
  selectMultinational,
  useAppConfigSelector,
  useAppMode,
} from '@hooks/useAppConfigSelectors';
import useBasket from '@hooks/useBasket';
import useBasketClean from '@hooks/useBasketClean';
import useBasketClear from '@hooks/useBasketClear';
import useBasketModifyDay from '@hooks/useBasketModifyDay';
import useCreateBasket from '@hooks/useCreateBasket';
import useCreateBasketAdditionalMealType from '@hooks/useCreateBasketAdditionalDishes';
import useCreateBasketAddon from '@hooks/useCreateBasketAddon';
import useCreateBasketAddonRange from '@hooks/useCreateBasketAddonRange';
import useCreateBasketDiet from '@hooks/useCreateBasketDiet';
import useCreateBasketDish from '@hooks/useCreateBasketDish';
import useDuplicateBasketDiet from '@hooks/useDuplicateBasketDiet';
import useOrderForm from '@hooks/useOrderForm';
import useUpdateAdditionalMealTypes from '@hooks/useUpdateAdditionalMealTypes';
import useUpdateBasketModify from '@hooks/useUpdateBasketModify';
import useValidateDiscountCode from '@hooks/useValidateDiscountCode';
import BasketService from '@services/Basket.service';
import getBasketAddonRowKey from '@utils/getBasketAddonRowKey';
import { pushDiscountCodeUpdateGTMEvent } from '@utils/gtm';
import hasOnlyDiscountExcludedDishes from '@utils/hasOnlyDiscountExcludedDishes';
import { isClient, isEmptyObject, sortDates } from '@utils/helpers';
import matchBasketAddonRow from '@utils/matchBasketAddonRow';
import showToast from '@utils/showToast';

import useSyncGtmCart from './hooks/useSyncGtmCart';

// eslint-disable-next-line @typescript-eslint/no-empty-function -- FIXME
export const BasketMethodsContext = createContext([{}, () => {}]);

const BasketMethodsProvider = ({ basketKey = 'basketNewOrder', children }) => {
  const { t } = useTranslation();

  const router = useRouter();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const syncGtmCart = useSyncGtmCart();

  const [failureCount, setFailureCount] = useState(0);
  const [showAbandonedModal, setShowAbandonedModal] = useState(true);

  const [
    isBasketNewOrderPaymentSummaryModalOpen,
    setIsBasketNewOrderPaymentSummaryModalOpen,
  ] = useState(false);

  const [
    isBasketEditOrderPaymentSummaryModalOpen,
    setIsBasketEditOrderPaymentSummaryModalOpen,
  ] = useState(false);

  // transforming basket by params beetwen static and normal
  const [transformBasketStatus, setTransformBasketStatus] = useState(null);

  const [pendingAddonKey, setPendingAddonKey] = useState(null);
  const pendingAddonSnapshotRef = useRef(null);

  const [shouldScrollIntoVariants, setShouldScrollIntoVariants] =
    useState(false);

  const basketQuery = useBasket({ basketKey });

  const updateBasketModifyQuery = useUpdateBasketModify({ basketKey });
  const mutateDietQuery = useCreateBasketDiet({ basketKey });
  const mutateDuplicateDietQuery = useDuplicateBasketDiet({ basketKey });

  const { mutateAsync: createBasket } = useCreateBasket({ basketKey });
  const { mutateAsync: basketCleanUp } = useBasketClean({ basketKey });
  const { mutateAsync: mutateBasketDiet } = mutateDietQuery;
  const { mutateAsync: mutateBasketDietDuplicate } = mutateDuplicateDietQuery;
  const { mutateAsync: mutateAdditionalMealTypes } =
    useUpdateAdditionalMealTypes({
      basketKey,
    });
  const { mutateAsync: updateBasketDish } = useCreateBasketDish({ basketKey });
  const { mutateAsync: updateBasketMealType } =
    useCreateBasketAdditionalMealType({ basketKey });
  const { mutateAsync: basketClear } = useBasketClear({
    basketKey,
    options: {
      // eslint-disable-next-line no-use-before-define -- FIXME
      onSuccess: () => dispatch(clearBasket()),
    },
  });

  const { mutateAsync: updateBasketAddon } = useCreateBasketAddon({
    basketKey,
  });
  const { mutateAsync: updateBasketAddonRange } = useCreateBasketAddonRange({
    basketKey,
  });

  const { mutateAsync: updateBasketModifyDay } = useBasketModifyDay({
    basketKey,
  });

  const validateDiscountCodeQuery = useValidateDiscountCode();
  const defaultDietElement = useDefaultDietElement();
  const [transformResponseBasketRedux] = useTransformResponseBasketRedux();
  const { mutateAsync: updateBasketModify } = updateBasketModifyQuery;
  const { mutateAsync: updateDiscountCode } = validateDiscountCodeQuery;

  const stateBasket = {
    basketNewOrder: {
      isPaymentSummaryModalOpen: isBasketNewOrderPaymentSummaryModalOpen,
      openPaymentSummaryModal: () =>
        setIsBasketNewOrderPaymentSummaryModalOpen(true),
      closePaymentSummaryModal: () =>
        setIsBasketNewOrderPaymentSummaryModalOpen(false),
    },
    basketEditOrder: {
      isPaymentSummaryModalOpen: isBasketEditOrderPaymentSummaryModalOpen,
      openPaymentSummaryModal: () =>
        setIsBasketEditOrderPaymentSummaryModalOpen(true),
      closePaymentSummaryModal: () =>
        setIsBasketEditOrderPaymentSummaryModalOpen(false),
    },
  }[basketKey];

  const { isOnlyShop, isDietShop, isDiet } = useAppMode();
  const { name: brandName } = useAppConfigSelector(selectBranding);
  const {
    defaultRegion: { currencyCode },
  } = useAppConfigSelector(selectMultinational);

  const { isBasketNewOrder, isBasketEditOrder } = useMemo(
    () => ({
      isBasketNewOrder: basketKey === 'basketNewOrder',
      isBasketEditOrder: basketKey === 'basketEditOrder',
    }),
    [basketKey]
  );

  const { isEmptyBasket } = useMemo(
    () => ({
      isEmptyBasket: isEmpty(basketQuery.data?.rows),
    }),

    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [basketQuery.dataUpdatedAt, basketQuery.errorUpdatedAt]
  );

  const prevIsEmptyBasket = usePrevious(isEmptyBasket);

  const isMutatingBasketClean = useIsMutating({
    mutationKey: [`${basketKey}Clean`],
  });
  const isMutatingBasketClear = useIsMutating({
    mutationKey: [`${basketKey}Clear`],
  });
  const isMutatingBasketAddModifyDay = useIsMutating({
    mutationKey: [`${basketKey}AddModifyDay`],
  });
  const isMutatingBasketCreate = useIsMutating({
    mutationKey: [`${basketKey}Create`],
  });
  const isMutatingBasketAddAddon = useIsMutating({
    mutationKey: [`${basketKey}AddAddon`],
  });
  const isMutatingBasketAddDiet = useIsMutating({
    mutationKey: [`${basketKey}AddDiet`],
  });
  const isMutatingBasketAddDish = useIsMutating({
    mutationKey: [`${basketKey}AddDish`],
  });
  const isMutatingBasketRestore = useIsMutating({
    mutationKey: [`${basketKey}Restore`],
  });
  const isMutatingBasketUpdateModify = useIsMutating({
    mutationKey: [`${basketKey}UpdateModify`],
  });
  const isMutatingBasketUpdatingAdditionalMealTypes = useIsMutating({
    mutationKey: [`${basketKey}UpdateAdditionalMealTypes`],
  });

  const isMutatingBasket = [
    isMutatingBasketClean,
    isMutatingBasketClear,
    isMutatingBasketAddModifyDay,
    isMutatingBasketCreate,
    isMutatingBasketAddAddon,
    isMutatingBasketAddDiet,
    isMutatingBasketAddDish,
    isMutatingBasketRestore,
    isMutatingBasketUpdateModify,
    isMutatingBasketUpdatingAdditionalMealTypes,
  ].some(Boolean);

  const isFetchingBasketClean = useIsFetching({
    queryKey: [`${basketKey}Clean`],
  });
  const isFetchingBasketClear = useIsFetching({
    queryKey: [`${basketKey}Clear`],
  });
  const isFetchingBasketAddModifyDay = useIsFetching({
    queryKey: [`${basketKey}AddModifyDay`],
  });
  const isFetchingBasketCreate = useIsFetching({
    queryKey: [`${basketKey}Create`],
  });
  const isFetchingBasketAddAddon = useIsFetching({
    queryKey: [`${basketKey}AddAddon`],
  });
  const isFetchingBasketAddDiet = useIsFetching({
    queryKey: [`${basketKey}AddDiet`],
  });
  const isFetchingBasketAddDish = useIsFetching({
    queryKey: [`${basketKey}AddDish`],
  });
  const isFetchingBasketRestore = useIsFetching({
    queryKey: [`${basketKey}Restore`],
  });
  const isFetchingBasketUpdateModify = useIsFetching({
    queryKey: [`${basketKey}UpdateModify`],
  });

  const isFetchingBasket = [
    isFetchingBasketClean,
    isFetchingBasketClear,
    isFetchingBasketAddModifyDay,
    isFetchingBasketCreate,
    isFetchingBasketAddAddon,
    isFetchingBasketAddDiet,
    isFetchingBasketAddDish,
    isFetchingBasketRestore,
    isFetchingBasketUpdateModify,
  ].some(Boolean);

  const basketActions = isBasketNewOrder
    ? basketNewOrderActions
    : basketEditOrderActions;

  const basketSelectors = isBasketNewOrder
    ? basketNewOrderSelectors
    : basketEditOrderSelectors;

  const disableTracking = useSelector(selectDisableTracking);
  const { isNextDisabled, currentStepIndex } = useSelector(selectTabs);
  const basketStore = useSelector(basketSelectors.selectBasket);

  const currentDietNewOrder = useSelector(
    basketNewOrderSelectors.selectCurrentDiet,
    shallowEqual
  );
  const currentDietEditOrder = useSelector(
    basketEditOrderSelectors.selectCurrentDiet,
    shallowEqual
  );

  const currentDiet = isBasketNewOrder
    ? currentDietNewOrder
    : currentDietEditOrder;

  const currentStepSlug = useSelector(state =>
    selectOrderTabsPick(state, ['currentStepSlug'])
  );

  const {
    addDiet: addDietBasket,
    addItemToBasket,
    setAdditionalMealTypes,
    updateAdditionalMealTypes,
    removeDiet: removeDietBasket,
    setDiscountCode,
    updateDiet: updateDietBasket,
    updateBasketWithCurrentDiet,
    clearBasket,
    setBasketValidity,
    setStoreFromQuery,
    setBasketMode,
    setActiveOrderDietIri,
  } = basketActions;

  const {
    discountCode,
    isPayActionSuccess,
    items: basketItems,
    useDiscountCode,
    basketMode,
  } = basketStore;

  const { dietElements } = basketItems;
  const firstDietElement = dietElements[0];

  const isBasketStatic = isBasketNewOrder && basketMode === BASKET_MODES.STATIC;
  const basketQueryId = basketQuery?.data?.id;
  const hostname = isClient
    ? `${window.location.protocol}//${window.location.host}`
    : '';

  const shareBasket = useCallback(
    async basketId => {
      const shareUrl = `${hostname}${ROUTE_URLS.NEW_ORDER_FORM}?basketId=${
        basketId || basketQueryId
      }`;

      await navigator.clipboard.writeText(shareUrl);
      showToast(t('$*common.copyToClipboard.copied'), {
        type: 'info',
      });

      // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
      createBasket({ basketId: basketId || basketQueryId }).then(response => {
        const transformedResponse = transformResponseBasketRedux(
          response,
          basketItems
        );
        dispatch(setStoreFromQuery(transformedResponse));
        // eslint-disable-next-line no-use-before-define -- FIXME
        dispatch(setBasketValidity(checkIsValid(response.uiRows)));

        router.replace(
          {
            pathname: router.pathname,
            query: {
              ...router.query,
              basketId: response?.id,
            },
          },
          undefined,
          { shallow: true }
        );
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [basketQueryId, createBasket, hostname, router?.pathname]
  );

  const basketCommonCatch = useCallback(
    ({
      data = {},
      scopeError = '',
      passedBasketQueryId,
      isInitializingError = false,
    } = {}) => {
      const violations = data?.violations ?? [];
      if (isEmpty(violations)) {
        return;
      }
      const hasErrorTestMode = some(violations, { propertyPath: 'testMode' });
      const hasErrorDiscountCode = some(violations, {
        propertyPath: 'discountCode.minimalOrderValue',
      });
      // max orders on day
      const errorCerberDays = violations.filter(
        // eslint-disable-next-line @stylistic/line-comment-position -- FIXME
        ({ propertyPath }) => propertyPath.includes('deliveryDay') // cerber from create-order
      );
      const hasErrorCerber = errorCerberDays.length > 0;

      if (!isInitializingError && hasErrorCerber) {
        const message = data?.['hydra:description'] ?? '';
        const errorDays = [...message.match(/\d{4}-\d{2}-\d{2}/gm)];

        // eslint-disable-next-line no-use-before-define -- FIXME
        refetchOrderForm();

        // check error is on the same step
        if (
          !(
            isDietShop &&
            scopeError === 'updateBasketDish' &&
            currentStepSlug === TAB_SLUGS.SELECT_DISHES
          )
        ) {
          dispatch(changeStepAndResetValid(0));
        }

        const id = passedBasketQueryId ?? basketQueryId;

        if (id) {
          // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
          basketCleanUp({ id }).then(response => {
            if (
              isDiet &&
              !isOnlyShop &&
              isBasketNewOrder &&
              isEmpty(response?.rows)
            ) {
              // eslint-disable-next-line no-use-before-define -- handler is defined later in this provider
              return handleClickClearStaticBasket(response.id, true);
            }

            const transformedResponse = transformResponseBasketRedux(
              response,
              basketItems
            );

            return dispatch(setStoreFromQuery(transformedResponse));
          });
        } else {
          createBasket();
        }

        // eslint-disable-next-line consistent-return -- FIXME
        return showToast(
          t('$*notification.basket.cerberReset.error', {
            days: errorDays.join(', '),
          }),
          { type: 'error', toastId: 'cerberResetInfo' }
        );
      }

      if (hasErrorTestMode) {
        dispatch(changeStepAndResetValid(0));
      }

      if (hasErrorDiscountCode) {
        dispatch(
          setDiscountCode({
            useDiscountCode: false,
            discountCode: { '@id': null, value: '' },
          })
        );
        queryClient.setQueryData('discountCode', {});
      }

      if (isInitializingError) {
        mutateBasketDiet({
          basket: passedBasketQueryId ?? basketQueryId,
          package: defaultDietElement.packageId,
          diet: defaultDietElement.dietId,
          variant: defaultDietElement.variantId,
          calorific: defaultDietElement.calorificId,
          paymentMode: defaultDietElement.paymentMode,
          dietLength: defaultDietElement.dietLength,
          deliveryDates: defaultDietElement.deliveryDates,
          firstDeliveryDay: defaultDietElement.firstDeliveryDay,
          useEcoContainers: defaultDietElement.useEcoContainers,
          addons: defaultDietElement.addons,
          optionChangeMenu: defaultDietElement.optionChangeMenu,
          saturdayInclude: defaultDietElement.saturdayInclude,
          sundayInclude: defaultDietElement.sundayInclude,
          allowStepWithAdditionalMealTypes:
            defaultDietElement.allowStepWithAdditionalMealTypes,
          hasDietsToSelectMenu: defaultDietElement?.hasDietsToSelectMenu,
          // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
        }).then(response => {
          setFailureCount(0);
          // eslint-disable-next-line no-use-before-define -- FIXME
          const newResponseDiet = getNewDietFromBasketResponse(response);

          dispatch(setBasketMode(BASKET_MODES.NORMAL));

          dispatch(
            updateDietBasket({
              '@id': newResponseDiet?.['@id'],
              '@type': newResponseDiet?.['@type'],
              ...defaultDietElement,
            })
          );
          // eslint-disable-next-line no-use-before-define -- FIXME
          dispatch(setBasketValidity(checkIsValid(response.uiRows)));
          dispatch(changeStepAndResetValid(0));
        });
      }

      // eslint-disable-next-line consistent-return, array-callback-return -- FIXME
      return violations.map(({ message, propertyPath }) => {
        showToast(message, { type: 'error', toastId: propertyPath });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [basketQueryId]
  );

  const basketStoreDietIris =
    basketStore.items.dietElements?.reduce(
      (acc, { dietId }) => [...acc, dietId],
      []
    ) ?? [];
  const basketQueryDietIris = basketQuery?.data?.rows?.reduce(
    (acc, item) => [...acc, item?.diet?.['@id']],
    []
  );

  const hasOnlyDiscountExcludedDishesInBasket = useMemo(
    () => hasOnlyDiscountExcludedDishes(basketQuery?.data?.rows),
    [basketQuery?.data?.rows]
  );

  const isAnyOfDietsInBasket = useCallback(
    (dietIds = []) => {
      // eslint-disable-next-line no-shadow -- FIXME
      const dietElements = isBasketStatic
        ? basketStoreDietIris
        : basketQueryDietIris;

      if (!Array.isArray(dietIds) || !Array.isArray(dietElements)) {
        return false;
      }

      return dietElements?.some(dietIri => dietIds.includes(dietIri));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [
      basketQuery.dataUpdatedAt,
      basketQuery.errorUpdatedAt,
      basketQueryDietIris,
      basketStoreDietIris,
      isBasketStatic,
    ]
  );

  const discountIri = discountCode?.['@id'];
  const basketQueryIri = basketQuery?.data?.['@id'];

  const handleNotifications = (response, existingItem = null) => {
    if (!isEmpty(response?.notifications)) {
      const notificationsKeys = Object.keys(response?.notifications || {});

      const haveInvalidAddons = !isEmpty(
        notificationsKeys.find(key => key.includes(NOTIFICATION_KEYS.R_ADDON))
      );

      const haveRemovedDiscountCode = !isEmpty(
        notificationsKeys.find(key =>
          key.includes(NOTIFICATION_KEYS.DISCOUNT_CODE_REMOVE)
        )
      );

      const haveRemovedAdditionalMealTypes = !isEmpty(
        notificationsKeys.find(
          key =>
            key.includes('R-DAILY-ITEM') ||
            key.includes(NOTIFICATION_KEYS.R_MEAL_TYPE_ITEM)
        )
      );

      if (haveInvalidAddons) {
        // eslint-disable-next-line no-use-before-define -- FIXME
        handleAddonsChangeUpdate({ existingItem, response });
      }

      if (haveRemovedAdditionalMealTypes) {
        const newAdditionalMealTypes = response?.rows.filter(
          row => row?.['@type'] === BASKET_ROWS_TYPES.MEAL_TYPE_ITEM
        );

        const newAdditionalMealTypesTransformedToRedux =
          newAdditionalMealTypes.reduce((acc, item) => {
            const { mealType, price, quantity, day } = item ?? {};

            return {
              ...acc,
              [mealType?.['@id']]: {
                ...acc[mealType?.['@id']],
                mealTypeName: mealType?.name,
                mealTypePrice: price?.afterDiscount,
                deliveryDates: {
                  ...acc[mealType?.['@id']]?.deliveryDates,
                  [day]: quantity,
                },
              },
            };
          }, {});

        dispatch(
          setAdditionalMealTypes({
            data: newAdditionalMealTypesTransformedToRedux,
            options: {
              isOnlyShop,
              // eslint-disable-next-line no-use-before-define -- FIXME
              connectedTo: basketActiveOrderDietIri,
              basketKey,
            },
          })
        );
      }

      if (haveRemovedDiscountCode) {
        // REDUX
        dispatch(
          setDiscountCode({
            useDiscountCode: false,
            discountCode: { '@id': null, value: '' },
          })
        );
        // REACT-QUERY
        queryClient.setQueryData(['discountCode'], {});

        // Sometimes query basket is changed after removing discount and redux basket should be updated
        const transformedResponse = transformResponseBasketRedux(
          response,
          basketItems
        );
        dispatch(setStoreFromQuery(transformedResponse));
      }

      (Object.values(response?.notifications || {}) || []).forEach(
        ({ message, type }) => {
          showToast(message, { type: type.toLowerCase() });
        }
      );
    }
  };

  const updateOrderBasketModify = useCallback(
    async ({
      basketId = null,
      payload,
      newDiscountCode,
      onSuccessCallback,
      onCatchCallback,
      // eslint-disable-next-line require-await -- FIXME
    }) => {
      const isResetPayload = isEmptyObject(payload);
      const mergePayload = isResetPayload
        ? {}
        : {
            ...(useDiscountCode ? { discountCode: discountIri } : {}),
            ...payload,
          };

      return (
        updateBasketModify({
          id: basketId || basketQueryId,
          payload: mergePayload,
        })
          // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
          .then(res => {
            onSuccessCallback?.(res);

            if (res.discountCode && !disableTracking) {
              pushDiscountCodeUpdateGTMEvent({
                discountCode: newDiscountCode,
                items: res?.rows,
                price: res?.price?.afterDiscount,
                brandName,
                currencyCode,
              });
            }

            if ('abandoned' in payload) {
              dispatch(refreshToken());
            }

            handleNotifications(res);
          })
          // eslint-disable-next-line promise/prefer-await-to-then, promise/prefer-await-to-callbacks -- FIXME
          .catch(error => {
            basketCommonCatch({ data: error?.response?.data });
            onCatchCallback?.(error);
          })
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [
      basketQueryId,
      discountIri,
      useDiscountCode,
      disableTracking,
      basketCommonCatch,
    ]
  );

  const modifyBasket = async payload => {
    let basketId = basketQueryId;

    if (isBasketStatic) {
      // eslint-disable-next-line no-use-before-define -- FIXME
      const { id } = await createBasketFromStaticBasket();

      basketId = id;
    }

    await updateBasketModify({
      id: basketId,
      payload: {
        ...payload,
        discountCode: discountIri,
      },
    }).then(response => {
      handleNotifications(response);
    });
  };

  const commonSetDiscountCode = useCallback(
    async code => {
      const newCode = code.replace(/  +/g, ' ')?.trim();

      let basketId;
      let basketIri;

      if (isBasketStatic) {
        // eslint-disable-next-line no-use-before-define -- FIXME
        const response = await createBasketFromStaticBasket();
        basketId = response.id;
        basketIri = response['@id'];
      }

      return (
        updateDiscountCode({
          code: newCode,
          basket: basketQuery?.data?.['@id'] || basketIri,
        })
          // eslint-disable-next-line promise/prefer-await-to-then, consistent-return, require-await -- FIXME
          .then(async response => {
            if (
              !isEmpty(response?.diets) &&
              !isAnyOfDietsInBasket(response?.diets)
            ) {
              return showToast(
                t(
                  '$*notification.validateDiscountCode.noDietsErrorMessage',
                  'Code does not work for ordered diet types'
                )
              );
            }

            if (hasOnlyDiscountExcludedDishesInBasket) {
              return showToast(
                t(
                  '$*notification.validateDiscountCode.dishesExcludedErrorMessage'
                )
              );
            }

            dispatch(
              setDiscountCode({
                useDiscountCode: true,
                discountCode: { '@id': response['@id'], value: newCode },
              })
            );

            updateOrderBasketModify({
              newDiscountCode: newCode,
              basketId,
              payload: {
                discountCode: response['@id'],
              },
            });

            showToast(t('$*notification.validateDiscountCode.success'), {
              type: 'success',
              toastId: 'validateDiscountCodeSuccess',
            });
          })
          // eslint-disable-next-line promise/prefer-await-to-then, promise/prefer-await-to-callbacks -- FIXME
          .catch(error => {
            throw error;
          })
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [
      updateOrderBasketModify,
      isAnyOfDietsInBasket,
      hasOnlyDiscountExcludedDishesInBasket,
      updateDiscountCode,
      isBasketStatic,
      firstDietElement,
    ]
  );

  // check code after login, when is error remove from basket
  const recheckDiscountCode = useCallback(
    // eslint-disable-next-line require-await -- FIXME
    async code => {
      const newCode = code.replace(/  +/g, ' ')?.trim();

      return (
        updateDiscountCode({ code: newCode, basket: basketQueryIri })
          // eslint-disable-next-line promise/prefer-await-to-then, promise/prefer-await-to-callbacks -- FIXME
          .catch(error => {
            const violations = error?.response?.data?.violations;

            if (!Array.isArray(violations)) {
              return;
            }

            const showViolationsToasts = () => {
              violations.forEach(({ propertyPath, message }, index) => {
                showToast(message, { toastId: `${propertyPath}_${index}` });
              });
            };

            dispatch(
              setDiscountCode({
                useDiscountCode: false,
                discountCode: { '@id': null, value: '' },
              })
            );

            if (!discountIri) {
              showViolationsToasts();
              updateOrderBasketModify({ payload: {} });
              return;
            }

            updateOrderBasketModify({
              payload: { discountCode: discountIri },
              onSuccessCallback: res => {
                if (!res?.discountCode) {
                  return;
                }

                showViolationsToasts();
                updateOrderBasketModify({ payload: {} });
              },
              onCatchCallback: updateError => {
                if (isEmpty(updateError?.response?.data?.violations)) {
                  showViolationsToasts();
                }
              },
            });
          })
      );
    },
    [
      basketQueryIri,
      discountIri,
      dispatch,
      setDiscountCode,
      updateDiscountCode,
      updateOrderBasketModify,
    ]
  );

  const checkBasketIsValidToPayment = useCallback(() => {
    if (
      isMutatingBasket ||
      isEmptyBasket ||
      (isBasketNewOrder && isNextDisabled)
    ) {
      return false;
    }

    return true;
  }, [isMutatingBasket, isEmptyBasket, isBasketNewOrder, isNextDisabled]);

  useEffect(() => {
    if (isBasketNewOrder) {
      if (
        prevIsEmptyBasket === false &&
        isEmptyBasket &&
        currentStepIndex !== 0
      ) {
        dispatch(changeStepAndResetValid(0));
        dispatch(basketNewOrderActions.clearBasket());

        showToast(t('$*notification.basketEmptyInvalid.error'), {
          type: 'warning',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, [isEmptyBasket, prevIsEmptyBasket, currentStepIndex, isBasketNewOrder]);

  const [checkIsValid] = useValidateBasket();

  const basketActiveOrderDietIri = basketStore.activeOrderDietIri;

  const getBasketQueryData = () => queryClient.getQueryData([basketKey]);

  const handleChangeProductQuantity = useCallback(
    async ({
      dishIri,
      day,
      quantity,
      dietIri = 'noDiet',
      connectedToIri,
      shouldDebounce = true,
    }) => {
      let basketIri;
      let firstDietIri;
      if (isBasketStatic) {
        // eslint-disable-next-line no-use-before-define -- FIXME
        const response = await createBasketFromStaticBasket();
        basketIri = response['@id'];
        firstDietIri = response.rows[0]['@id'];
      }

      const basket = getBasketQueryData();

      dispatch(
        addItemToBasket({
          item: { day, dietIri, iri: dishIri, quantity },
          options: {
            isOnlyShop,
            connectedTo:
              firstDietIri ?? connectedToIri ?? basketActiveOrderDietIri,
            basketKey,
          },
        })
      );

      const connectedTo =
        // eslint-disable-next-line no-negated-condition -- FIXME
        connectedToIri !== 'notConnected' ? connectedToIri : null;

      return (
        updateBasketDish({
          shouldDebounce,
          basket: basketIri || basket['@id'],
          dishSize: dishIri,
          day,
          quantity,
          ...(isBasketNewOrder
            ? {
                connectedTo:
                  firstDietIri ??
                  connectedTo ??
                  basketActiveOrderDietIri ??
                  null,
              }
            : {}),
          // eslint-disable-next-line no-negated-condition -- FIXME
          ...(dietIri !== 'noDiet' ? { clientDiet: dietIri } : {}),
        })
          // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
          .then(response => {
            syncGtmCart(basket, response);

            dispatch(setBasketValidity(checkIsValid(response.uiRows)));
            return true;
          })
          // eslint-disable-next-line promise/prefer-await-to-then, promise/prefer-await-to-callbacks -- FIXME
          .catch(error => {
            basketCommonCatch({
              data: error?.response?.data,
              scopeError: 'updateBasketDish',
            });
            return false;
          })
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [
      basketActiveOrderDietIri,
      basketKey,
      brandName,
      isBasketStatic,
      disableTracking,
      isBasketNewOrder,
      isOnlyShop,
      firstDietElement,
    ]
  );

  const handleChangeMealTypesQuantity = useCallback(
    async ({ dishIri, day, quantity, dietIri = 'noDiet', connectedToIri }) => {
      let basketIri;
      let firstDietIri;
      if (isBasketStatic) {
        // eslint-disable-next-line no-use-before-define -- FIXME
        const response = await createBasketFromStaticBasket();
        basketIri = response['@id'];
        firstDietIri = response.rows[0]['@id'];
      }

      const basket = getBasketQueryData();

      dispatch(
        addItemToBasket({
          item: { day, dietIri, iri: dishIri, quantity },
          options: {
            isOnlyShop,
            connectedTo:
              firstDietIri ?? connectedToIri ?? basketActiveOrderDietIri,
            basketKey,
          },
        })
      );

      const connectedTo =
        // eslint-disable-next-line no-negated-condition -- FIXME
        connectedToIri !== 'notConnected' ? connectedToIri : null;

      return (
        updateBasketMealType({
          basket: basketIri || basket['@id'],
          mealType: dishIri,
          day,
          quantity,
          ...(isBasketNewOrder
            ? {
                connectedTo:
                  firstDietIri ??
                  connectedTo ??
                  basketActiveOrderDietIri ??
                  null,
              }
            : {}),
          // eslint-disable-next-line no-negated-condition -- FIXME
          ...(dietIri !== 'noDiet' ? { clientDiet: dietIri } : {}),
        })
          // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
          .then(response => {
            showToast(
              t('$*extraPortions.mainComponent.stepTwo.modal.finishedToast'),
              { type: 'success' }
            );

            dispatch(setBasketValidity(checkIsValid(response.uiRows)));
          })
          // eslint-disable-next-line promise/prefer-await-to-then, promise/prefer-await-to-callbacks -- FIXME
          .catch(error => {
            basketCommonCatch({
              data: error?.response?.data,
              scopeError: 'updateBasketDish',
            });
          })
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [
      basketActiveOrderDietIri,
      basketKey,
      brandName,
      currencyCode,
      disableTracking,
      isBasketNewOrder,
      isOnlyShop,
      firstDietElement,
    ]
  );

  const productItems = useMemo(
    () =>
      isOnlyShop || isBasketEditOrder
        ? basketItems
        : currentDiet.connectedElements ?? {},
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [
      isOnlyShop,
      isBasketEditOrder,
      basketItems,
      // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
      JSON.stringify(currentDiet.connectedElements),
      // eslint-disable-next-line @stylistic/line-comment-position -- FIXME
    ] // TO CHECK !!
  );

  // TO REMOVE
  const checkIsProductSelected = useCallback(
    ({ product, dietIri = 'noDiet', selectedDate }) => {
      const dayItems = productItems?.[dietIri]?.[selectedDate];

      if (!isEmpty(dayItems)) {
        const items = Object.keys(dayItems);
        const productHasDishSize = product.sizes.some(size =>
          items.includes(size['@id'])
        );

        return productHasDishSize;
      }

      return false;
    },
    [productItems]
  );

  const handleChangeAddonQuantity = useCallback(
    // eslint-disable-next-line require-await -- FIXME
    async ({ addonIri, day, quantity, dietIri = 'noDiet', connectedToIri }) => {
      const basket = getBasketQueryData();

      const connectedTo = connectedToIri || basketActiveOrderDietIri;

      const pendingRowKey = getBasketAddonRowKey({
        addonIri,
        day,
        dietIri,
        connectedToIri,
      });

      if (pendingAddonSnapshotRef.current?.key !== pendingRowKey) {
        pendingAddonSnapshotRef.current = {
          key: pendingRowKey,
          basket: getBasketQueryData(),
        };
      }

      setPendingAddonKey(pendingRowKey);

      const clearPendingAddonKey = () => {
        setPendingAddonKey(prev => (prev === pendingRowKey ? null : prev));
        if (pendingAddonSnapshotRef.current?.key === pendingRowKey) {
          pendingAddonSnapshotRef.current = null;
        }
      };

      const matchAddonRow = matchBasketAddonRow({ addonIri, day, dietIri });

      const previousQuantity =
        (basket?.rows ?? []).find(matchAddonRow)?.quantity ?? 0;

      dispatch(
        addItemToBasket({
          item: { day, dietIri, iri: addonIri, quantity },
          options: {
            isOnlyShop,
            connectedTo,
            basketKey,
          },
        })
      );

      const buildBasketWithAddonQuantity = (basketData, targetQuantity) => {
        const rows = basketData?.rows ?? [];

        if (rows.filter(matchAddonRow).length !== 1) {
          return null;
        }

        const nextRows = rows
          .map(row =>
            matchAddonRow(row) ? { ...row, quantity: targetQuantity } : row
          )
          .filter(row => !(targetQuantity === 0 && matchAddonRow(row)));

        return {
          ...basketData,
          rows: nextRows,
          uiRows: BasketService.transformResponseBasketUI({
            ...basketData,
            rows: nextRows,
          }),
        };
      };

      const optimisticBasket = buildBasketWithAddonQuantity(basket, quantity);

      if (optimisticBasket) {
        queryClient.setQueryData([basketKey], optimisticBasket);
      }

      updateBasketAddon({
        shouldDebounce: true,
        basket: basket['@id'],
        addon: addonIri,
        day,
        quantity,
        ...(isBasketNewOrder ? { connectedTo: connectedTo || null } : {}),
        // eslint-disable-next-line no-negated-condition -- FIXME
        ...(dietIri !== 'noDiet' ? { clientDiet: dietIri } : {}),
      })
        // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
        .then(response => {
          syncGtmCart(basket, response);

          dispatch(setBasketValidity(checkIsValid(response.uiRows)));

          clearPendingAddonKey();
        })
        // eslint-disable-next-line promise/prefer-await-to-then, promise/prefer-await-to-callbacks -- FIXME
        .catch(error => {
          if (error === ERROR_TYPES.STALE_PROMISE) {
            return;
          }

          const snapshotBasket =
            pendingAddonSnapshotRef.current?.key === pendingRowKey
              ? pendingAddonSnapshotRef.current.basket
              : null;

          const targetBasket =
            snapshotBasket ??
            buildBasketWithAddonQuantity(
              getBasketQueryData(),
              previousQuantity
            );

          if (targetBasket) {
            queryClient.setQueryData([basketKey], targetBasket);
            dispatch(
              setStoreFromQuery(
                transformResponseBasketRedux(targetBasket, basketItems)
              )
            );
          }

          clearPendingAddonKey();

          basketCommonCatch({ data: error?.response?.data });
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [
      basketActiveOrderDietIri,
      basketKey,
      brandName,
      disableTracking,
      isBasketNewOrder,
      isOnlyShop,
    ]
  );

  const addBasketItemAddonOnSinglePaymentDietMode = async ({
    addon,
    quantity,
    days,
    clientDiet = null,
  }) => {
    if (isBasketStatic) {
      // eslint-disable-next-line no-use-before-define -- FIXME
      await createBasketFromStaticBasket();
    }

    const isBasketCreatedFromStaticBasket = isBasketStatic;
    const basket = getBasketQueryData();
    const fistBasketDiet = basket.rows.find(
      row => row['@type'] === BASKET_ROWS_TYPES.DIET_ITEM
    );
    const connectedTo = isBasketCreatedFromStaticBasket
      ? fistBasketDiet['@id']
      : basketActiveOrderDietIri;
    const response = await updateBasketAddonRange({
      basket: basket['@id'],
      connectedTo,
      clientDiet,
      addon,
      quantity,
      days,
    });

    const transformedResponse = transformResponseBasketRedux(
      response,
      basketItems
    );
    dispatch(setStoreFromQuery(transformedResponse));
    dispatch(setBasketValidity(checkIsValid(response.uiRows)));

    handleNotifications(response);

    if (isEmpty(response.notifications)) {
      showToast(t('$*addonHasBeenSuccessfullyAdded'), {
        type: 'success',
      });
    }

    syncGtmCart(basket, response);

    return response;
  };

  // TO REMOVE
  const checkIsAddonSelected = useCallback(
    ({ addon, dietIri = 'noDiet', selectedDate }) => {
      const dayItems = basketItems?.[dietIri]?.[selectedDate];

      if (!isEmpty(dayItems)) {
        const items = Object.keys(dayItems);
        return items.includes(addon['@id']);
      }

      return false;
    },
    [basketItems]
  );

  const handleUpdateBasketModifyDay = useCallback(
    // eslint-disable-next-line require-await -- FIXME
    async payload => {
      const basket = getBasketQueryData();

      if (!basket || isEmpty(basket)) {
        return;
      }

      // eslint-disable-next-line consistent-return -- FIXME
      return updateBasketModifyDay({
        payload: {
          basket: basket['@id'],
          ...payload,
        },
        // eslint-disable-next-line promise/prefer-await-to-then, promise/prefer-await-to-callbacks -- FIXME
      }).catch(error => {
        basketCommonCatch({ data: error?.response?.data });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [basketCommonCatch, basketKey]
  );

  const basketStoreDiets = dietElements?.filter(
    row => row['@type'] === 'BasketItemDiet'
  );
  const basketStoreDietIds = basketStoreDiets.map(row => row['@id']);

  const handleRemoveBasketDiet = useCallback(
    // eslint-disable-next-line require-await -- FIXME
    async dietIri => {
      const basket = getBasketQueryData();

      if (!basket || isEmpty(basket)) {
        return;
      }

      // eslint-disable-next-line no-shadow -- FIXME
      const currentDiet = dietElements?.find(diet => diet['@id'] === dietIri);

      const basketDiet = {
        addons: currentDiet?.addons,
        calorific: currentDiet?.calorificId,
        deliveryDates: sortDates(currentDiet?.deliveryDates ?? []),
        diet: currentDiet?.dietId,
        dietLength: currentDiet?.dietLength,
        firstDeliveryDay: currentDiet?.firstDeliveryDay,
        optionChangeMenu: currentDiet?.optionChangeMenu,
        package: currentDiet?.packageId,
        paymentMode: currentDiet?.paymentMode,
        quantity: 0,
        saturdayInclude: currentDiet?.saturdayInclude,
        sundayInclude: currentDiet?.sundayInclude,
        useEcoContainers: currentDiet?.useEcoContainers,
        variant: currentDiet?.variantId,
        mealTypes: currentDiet?.mealTypes,
        allowStepWithAdditionalMealTypes:
          currentDiet?.allowStepWithAdditionalMealTypes,
        hasDietsToSelectMenu: currentDiet?.hasDietsToSelectMenu,
      };

      mutateBasketDiet({
        basket: basket['@id'],
        existingItem: dietIri ?? null,
        ...basketDiet,
        // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
      }).then(response => {
        syncGtmCart(basket, response, { asSeparateEvents: false });

        dispatch(setBasketValidity(checkIsValid(response.uiRows)));
        dispatch(removeDietBasket(dietIri));
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [basketKey, brandName, dietElements, disableTracking]
  );

  const getNewDietFromBasketResponse = useCallback(
    response => {
      const responseDiets = response?.rows?.filter(
        row => row['@type'] === 'BasketItemDiet'
      );

      const diffResponseDiets = responseDiets.filter(
        row => !basketStoreDietIds.includes(row['@id'])
      );

      const newResponseDiet = diffResponseDiets?.at(-1) ?? {
        '@id': null,
        '@type': null,
      };

      return newResponseDiet;
    },
    [basketStoreDietIds]
  );

  const handleAddBasketDiet = useCallback(
    // eslint-disable-next-line require-await -- FIXME
    async basketItem => {
      const basket = getBasketQueryData();

      if (!basket || isEmpty(basket)) {
        return;
      }

      // eslint-disable-next-line consistent-return -- FIXME
      return (
        mutateBasketDiet({
          shouldDebounce: true,
          basket: basket?.['@id'],
          ...basketItem,
        })
          // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
          .then(response => {
            syncGtmCart(basket, response);

            setFailureCount(0);

            const newResponseDiet = getNewDietFromBasketResponse(response);
            dispatch(
              addDietBasket({
                '@id': newResponseDiet?.['@id'],
                '@type': newResponseDiet?.['@type'],
                addons: basketItem?.addons,
                calorificId: basketItem?.calorific,
                deliveryDates: sortDates(basketItem?.deliveryDates ?? []),
                dietId: basketItem?.diet,
                dietLength: basketItem?.dietLength,
                dietLengthMode:
                  basketItem?.dietLengthMode ?? DIET_LENGTH_MODE.CUSTOM,
                firstDeliveryDay: basketItem?.firstDeliveryDay,
                optionChangeMenu: basketItem?.optionChangeMenu,
                packageLineFilters: basketItem?.packageLineFilters,
                packageId: basketItem?.package,
                paymentMode: basketItem?.paymentMode,
                useEcoContainers: basketItem?.useEcoContainers,
                variantId: basketItem?.variant,
                sundayInclude: basketItem?.sundayInclude ?? true,
                saturdayInclude: basketItem?.saturdayInclude ?? true,
                deliveryType: basketItem?.deliveryType ?? 'ADDRESS',
                address: basketItem?.address ?? {},
                pickUpPoint: basketItem?.pickUpPoint ?? {},
                mealTypes: basketItem?.mealTypes ?? [],
                allowStepWithAdditionalMealTypes:
                  basketItem?.allowStepWithAdditionalMealTypes,
                hasDietsToSelectMenu: basketItem?.hasDietsToSelectMenu,
              })
            );

            dispatch(setBasketValidity(checkIsValid(response.uiRows)));
          })
          // eslint-disable-next-line promise/prefer-await-to-then, promise/prefer-await-to-callbacks -- FIXME
          .catch(error => {
            if (error === ERROR_TYPES.STALE_PROMISE) {
              return;
            }

            setFailureCount(failureCount + 1);

            const isCancelledError = error && Object.hasOwn(error, 'silent');

            if (!isCancelledError) {
              basketCommonCatch({ data: error?.response?.data });
            }
          })
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [
      basketCommonCatch,
      basketKey,
      brandName,
      disableTracking,
      failureCount,
      getNewDietFromBasketResponse,
    ]
  );

  const handleUpdateBasketDiet = useCallback(
    async ({
      existingItem = null,
      scrollIntoVariants = false,
      shouldDebounce = false,
      syncStoreFromResponse = false,
      ...updatedProps
    } = {}) => {
      const basket = getBasketQueryData();

      const { diets = [] } = queryClient.getQueryData(['orderForm']);

      setShouldScrollIntoVariants(scrollIntoVariants);

      if (existingItem === null && isEmpty(basket?.rows) && !isBasketStatic) {
        return handleAddBasketDiet({
          ...defaultDietElement,
          ...updatedProps,
        });
      }

      const basketCurrentDiet = existingItem
        ? basketItems?.dietElements?.find(row => row['@id'] === existingItem)
        : currentDiet;

      const currentQueryDiet = diets.find(
        ({ '@id': iri }) => iri === basketCurrentDiet.dietId
      );
      const currentVariant = currentQueryDiet?.variants?.find(
        ({ '@id': iri }) => iri === basketCurrentDiet.variantId
      );
      const currentCalorific = currentVariant?.calories?.find(
        ({ '@id': iri }) => iri === basketCurrentDiet.calorificId
      );

      const previousDiet = {
        addons: basketCurrentDiet?.addons,
        calorific: basketCurrentDiet?.calorificId,
        calorificName: currentCalorific?.name,
        deliveryDates: sortDates(basketCurrentDiet?.deliveryDates ?? []),
        diet: basketCurrentDiet?.dietId,
        dietLength: basketCurrentDiet?.dietLength,
        firstDeliveryDay: basketCurrentDiet?.firstDeliveryDay,
        name: basketCurrentDiet?.name,
        optionChangeMenu: basketCurrentDiet?.optionChangeMenu,
        package: basketCurrentDiet?.packageId,
        packageLineFilters: basketCurrentDiet?.packageLineFilters,
        paymentMode: basketCurrentDiet?.paymentMode,
        useEcoContainers: basketCurrentDiet?.useEcoContainers,
        variant: basketCurrentDiet?.variantId,
        variantName: currentVariant?.name,
        sundayInclude: basketCurrentDiet?.sundayInclude,
        saturdayInclude: basketCurrentDiet?.saturdayInclude,
        delivery: basketCurrentDiet?.delivery,
        mealTypes: basketCurrentDiet?.mealTypes,
        allowStepWithAdditionalMealTypes:
          basketCurrentDiet?.allowStepWithAdditionalMealTypes,
        hasDietsToSelectMenu: basketCurrentDiet?.hasDietsToSelectMenu,
      };

      // clean object from undefined values
      const updatedDiet = pickBy(
        {
          ...previousDiet,
          ...updatedProps,
        },
        v => v !== undefined
      );

      if (isBasketStatic) {
        if (updatedProps.delivery) {
          // eslint-disable-next-line no-use-before-define -- FIXME
          return await createBasketFromStaticBasket({
            delivery: updatedProps.delivery,
          });
        }

        if (updatedProps.addons) {
          // eslint-disable-next-line no-use-before-define -- FIXME
          return await createBasketFromStaticBasket({
            addons: updatedProps.addons,
          });
        }

        const updatedQueryDiet = diets.find(
          ({ '@id': iri }) => iri === updatedDiet.diet
        );
        const updatedVariant = updatedQueryDiet?.variants?.find(
          ({ '@id': iri }) => iri === updatedDiet.variant
        );
        const updatedCalorific = updatedVariant?.calories?.find(
          ({ '@id': iri }) => iri === updatedDiet.calorific
        );

        const dietInReduxStoreFormat = {
          id: updatedQueryDiet?.id,
          addons: updatedDiet?.addons,
          calorificId: updatedDiet?.calorific,
          calorificName: updatedCalorific?.name,
          deliveryDates: sortDates(updatedDiet?.deliveryDates ?? []),
          dietId: updatedDiet?.diet,
          dietLength: updatedDiet?.dietLength,
          existingItem,
          firstDeliveryDay: updatedDiet?.firstDeliveryDay,
          name: updatedDiet?.name,
          optionChangeMenu: updatedDiet?.optionChangeMenu,
          packageId: updatedDiet?.package,
          packageLineFilters: updatedDiet?.packageLineFilters,
          paymentMode: updatedDiet?.paymentMode,
          useEcoContainers: updatedDiet?.useEcoContainers,
          variantId: updatedDiet?.variant,
          variantName: updatedVariant?.name,
          sundayInclude: updatedDiet?.sundayInclude ?? true,
          saturdayInclude: updatedDiet?.saturdayInclude ?? true,
          delivery: updatedDiet?.delivery,
          mealTypes: updatedDiet?.mealTypes,
          allowStepWithAdditionalMealTypes:
            updatedDiet?.allowStepWithAdditionalMealTypes,
          hasDietsToSelectMenu: updatedDiet?.hasDietsToSelectMenu,
        };

        const basketPrices = getBasketPrices({
          dietElements: [dietInReduxStoreFormat],
          // eslint-disable-next-line no-use-before-define -- FIXME
          testMode,
          queryClient,
        });

        return dispatch(
          updateDietBasket({
            ...dietInReduxStoreFormat,
            price: basketPrices?.price,
          })
        );
      }

      if (!basket || isEmpty(basket)) {
        // eslint-disable-next-line consistent-return -- FIXME
        return;
      }

      dispatch(
        updateDietBasket({
          addons: updatedDiet?.addons,
          calorificId: updatedDiet?.calorific,
          deliveryDates: sortDates(updatedDiet?.deliveryDates ?? []),
          dietId: updatedDiet?.diet,
          dietLength: updatedDiet?.dietLength,
          existingItem,
          firstDeliveryDay: updatedDiet?.firstDeliveryDay,
          name: updatedDiet?.name,
          optionChangeMenu: updatedDiet?.optionChangeMenu,
          packageId: updatedDiet?.package,
          packageLineFilters: updatedDiet?.packageLineFilters,
          paymentMode: updatedDiet?.paymentMode,
          useEcoContainers: updatedDiet?.useEcoContainers,
          variantId: updatedDiet?.variant,
          sundayInclude: updatedDiet?.sundayInclude ?? true,
          saturdayInclude: updatedDiet?.saturdayInclude ?? true,
          delivery: updatedDiet?.delivery,
          mealTypes: updatedDiet?.mealTypes,
          allowStepWithAdditionalMealTypes:
            updatedDiet?.allowStepWithAdditionalMealTypes,
          hasDietsToSelectMenu: updatedDiet?.hasDietsToSelectMenu,
        })
      );

      return (
        mutateBasketDiet({
          basket: basket['@id'],
          existingItem: existingItem ?? currentDiet?.['@id'] ?? null,
          shouldDebounce,
          ...updatedDiet,
        })
          // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
          .then(response => {
            syncGtmCart(basket, response);

            if (syncStoreFromResponse) {
              dispatch(
                setStoreFromQuery(
                  transformResponseBasketRedux(response, basketItems)
                )
              );
            }

            dispatch(setBasketValidity(checkIsValid(response.uiRows)));

            handleNotifications(response);
          })
          // eslint-disable-next-line promise/prefer-await-to-then, promise/prefer-await-to-callbacks -- FIXME
          .catch(error => {
            if (error === ERROR_TYPES.STALE_PROMISE) {
              return;
            }

            dispatch(
              updateDietBasket({
                addons: previousDiet?.addons,
                calorificId: previousDiet?.calorific,
                deliveryDates: sortDates(previousDiet?.deliveryDates ?? []),
                dietId: previousDiet?.diet,
                dietLength: previousDiet?.dietLength,
                existingItem,
                firstDeliveryDay: previousDiet?.firstDeliveryDay,
                name: previousDiet?.name,
                optionChangeMenu: previousDiet?.optionChangeMenu,
                packageId: previousDiet?.package,
                paymentMode: previousDiet?.paymentMode,
                useEcoContainers: previousDiet?.useEcoContainers,
                variantId: previousDiet?.variant,
                sundayInclude: previousDiet?.sundayInclude ?? true,
                saturdayInclude: previousDiet?.saturdayInclude ?? true,
                mealTypes: previousDiet?.mealTypes,
                allowStepWithAdditionalMealTypes:
                  previousDiet?.allowStepWithAdditionalMealTypes,
                hasDietsToSelectMenu: previousDiet?.hasDietsToSelectMenu,

                // eslint-disable-next-line no-nested-ternary -- FIXME
                ...(isEmpty(previousDiet?.delivery)
                  ? { address: {}, pickUpPoint: {}, deliveryType: 'ADDRESS' }
                  : previousDiet?.delivery?.address
                  ? {
                      address: previousDiet?.delivery?.address,
                      pickUpPoint: {},
                      deliveryType: 'ADDRESS',
                    }
                  : {
                      address: {},
                      pickUpPoint: previousDiet?.delivery?.pickUpPoint,
                      deliveryType: 'PICKUP_POINT',
                    }),
              })
            );

            basketCommonCatch({ data: error?.response?.data });
          })
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [
      basketCommonCatch,
      basketItems,
      basketKey,
      brandName,
      currentDiet,
      disableTracking,
      isBasketStatic,
    ]
  );

  const handleAddonsChangeUpdate = ({ existingItem, response }) => {
    const currentExistingItem = existingItem ?? currentDiet?.['@id'] ?? null;
    const rowInResponse = (response?.rows ?? []).find(
      row => row?.['@id'] === currentExistingItem
    );

    dispatch(
      updateDietBasket({
        addons: rowInResponse?.addons,
      })
    );
  };

  /** *************************************
   * START: CHANGE TEST MODE [DIET FULL] *
   ***************************************/

  const {
    data: {
      customDietLengthEnabled,
      days: { standardDays = {}, testDays = {} } = {},
      predefinedDietLengthEnabled,
    } = {},
    refetch: refetchOrderForm,
  } = useOrderForm({ enabled: false });

  const testDaysDefault = testDays?.default;
  const firstTestDayOption = first(testDays?.options);
  const minTestDay = testDays?.min;
  const standardDaysDefault = standardDays?.default;
  const predefinedDaysDefault = (standardDays?.options ?? [])?.[0];

  const handleChangeTestMode = useCallback(
    ({
      basketId,
      isEnabled,
      isEnabledUpdateBasket = true,
      deliveryDates = [],
      ignoreStaticBasket = false,
      customDietLength,
    }) => {
      if (isBasketStatic && !ignoreStaticBasket) {
        const dietLength = isEnabled
          ? testDaysDefault ?? firstTestDayOption
          : standardDaysDefault;

        const basketPrices = getBasketPrices({
          dietElements: basketStore.items.dietElements,
          testMode: isEnabled,
          queryClient,
        });

        return dispatch(
          updateBasketWithCurrentDiet({
            testMode: isEnabled,
            currentDiet: {
              deliveryDates: sortDates(deliveryDates ?? []),
              dietLength,
              price: basketPrices?.price,
            },
          })
        );
      }

      return updateOrderBasketModify({
        basketId,
        payload: { testMode: isEnabled },
        // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
      }).then(() => {
        if (isEnabledUpdateBasket) {
          // eslint-disable-next-line no-nested-ternary -- FIXME
          const dietLength = isEnabled
            ? testDaysDefault ?? firstTestDayOption ?? minTestDay
            : predefinedDietLengthEnabled && !customDietLengthEnabled
            ? predefinedDaysDefault ?? standardDaysDefault
            : standardDaysDefault;

          handleUpdateBasketDiet({
            dietLength: customDietLength ?? dietLength,
            deliveryDates,
          });
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [
      basketMode,
      firstTestDayOption,
      handleUpdateBasketDiet,
      isBasketStatic,
      minTestDay,
      standardDaysDefault,
      testDaysDefault,
      updateOrderBasketModify,
      customDietLengthEnabled,
      predefinedDietLengthEnabled,
    ]
  );

  /** ************************************
   * END: CHANGE TEST MODE [DIET FULL] *
   *************************************/

  const handleBasketClear = useCallback(
    // eslint-disable-next-line consistent-return, require-await -- FIXME
    async ({ basketId, disableToast = false } = {}) => {
      const basket = getBasketQueryData();
      if (basketId || basketQueryId) {
        return basketClear({
          id: basketId || basketQueryId,
          disableToast,
          // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
        }).then(response => {
          syncGtmCart(basket, response, { asSeparateEvents: false });
          dispatch(setBasketValidity(checkIsValid(response.uiRows)));

          if (isBasketEditOrder) {
            dispatch(setStoreFromQuery({ items: { dietElements: [] } }));
          }
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [basketQueryId, brandName, disableTracking, isBasketEditOrder]
  );

  // clear for static basket
  const [getStaticBasketStore] = useStaticBasketStore();

  // eslint-disable-next-line no-shadow -- FIXME
  const setBasketAbandoned = async (basketId, basketKey) => {
    const basket = getBasketQueryData();
    try {
      const response = await updateBasketModify({
        id: basketId,
        payload: { abandoned: true },
      });

      syncGtmCart(basket, {}, { asSeparateEvents: false });

      queryClient.setQueryData([basketKey], {});

      return response;
    } catch (error) {
      return error;
    }
  };

  const handleClickClearStaticBasket = useCallback(
    async (maybeBasketId, disableToast = false) => {
      const basketId =
        typeof maybeBasketId === 'string' ? maybeBasketId : basketQueryId;

      if (!isDiet || isBasketEditOrder) {
        return handleBasketClear({});
      }

      // default settings
      const noApiBasketStore = getStaticBasketStore();

      if (!disableToast) {
        showToast(
          t(
            '$*notification.basketClear.clear.success',
            'Koszyk został poprawnie wyczyszczony.'
          ),
          { type: 'success' }
        );
      }

      if (basketId) {
        await setBasketAbandoned(basketId, basketKey);
      }

      setTransformBasketStatus('NORMAL_TO_STATIC');

      dispatch(changeStepAndResetValid(0));
      return dispatch(setStoreFromQuery(noApiBasketStore));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [basketQueryId, getStaticBasketStore, handleBasketClear]
  );

  const initBasket = useCallback(
    async ({
      basketId,
      createFromLatestDiet = false,
      ecommerceDiet = null,
      initDate = null,
      forceCreate = false,
      noBasketMode = false,
      oldBasketParams = {},
      withDefaultDiet = false,
      isSyncGtmCartDisabled = false,
    } = {}) => {
      const persistedBasket = getBasketQueryData();

      if (noBasketMode && isDiet) {
        if (oldBasketParams && basketQueryId) {
          setTransformBasketStatus('INIT_NORMAL_TO_STATIC');
          await setBasketAbandoned(basketQueryId, basketKey);
        }

        const noApiBasketStore = getStaticBasketStore(oldBasketParams);

        return dispatch(setStoreFromQuery(noApiBasketStore));
      }

      if (ecommerceDiet || createFromLatestDiet) {
        return createBasket({
          ...(ecommerceDiet ? { ecommerceDiet } : {}),
          ...(initDate ? { initDate } : {}),
          createFromLatestDiet,
          withDefaultDiet,
          // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
        }).then(response => {
          if (!isSyncGtmCartDisabled) {
            syncGtmCart({}, response);
          }

          const transformedResponse = transformResponseBasketRedux(
            response,
            basketItems
          );
          dispatch(setStoreFromQuery(transformedResponse));
          dispatch(setBasketValidity(checkIsValid(response.uiRows)));

          return response;
        });
      }

      if (forceCreate || basketId) {
        // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
        return createBasket({ basketId }).then(async response => {
          if (!isEmpty(basketId) && isEmpty(response?.rows) && !isOnlyShop) {
            return await handleClickClearStaticBasket(response.id);
          }

          handleNotifications(response);

          const transformedResponse = transformResponseBasketRedux(
            response,
            basketItems
          );

          dispatch(setStoreFromQuery(transformedResponse));
          dispatch(setBasketValidity(checkIsValid(response.uiRows)));

          return response;
        });
      }

      if (persistedBasket?.id && !isPayActionSuccess) {
        let response;

        try {
          response = await basketCleanUp({ id: persistedBasket.id });
        } catch (error) {
          if (isDiet && !isOnlyShop && isBasketNewOrder) {
            queryClient.setQueryData([basketKey], {});
            dispatch(changeStepAndResetValid(0));

            return dispatch(setStoreFromQuery(getStaticBasketStore()));
          }

          throw error;
        }

        if (
          isDiet &&
          !isOnlyShop &&
          isBasketNewOrder &&
          isEmpty(response?.rows)
        ) {
          return handleClickClearStaticBasket(response.id);
        }

        handleNotifications(response);

        const transformedResponse = transformResponseBasketRedux(
          response,
          basketItems
        );
        dispatch(setStoreFromQuery(transformedResponse));
        dispatch(setBasketValidity(checkIsValid(response.uiRows)));

        return response;
      }

      // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
      return createBasket({ withDefaultDiet }).then(response => {
        const transformedResponse = transformResponseBasketRedux(
          response,
          basketItems
        );
        dispatch(setStoreFromQuery(transformedResponse));
        dispatch(setBasketValidity(checkIsValid(response.uiRows)));

        return response;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [basketKey, isPayActionSuccess, getStaticBasketStore]
  );

  const createBasketFromStaticBasket = useCallback(
    async overrideDiet => {
      const basket = getBasketQueryData();

      setTransformBasketStatus('STATIC_TO_NORMAL');

      const response = await createBasket();

      return (
        mutateBasketDiet({
          basket: response['@id'],
          package: firstDietElement.packageId,
          diet: firstDietElement.dietId,
          variant: firstDietElement.variantId,
          calorific: firstDietElement.calorificId,
          paymentMode: firstDietElement.paymentMode,
          dietLength: firstDietElement.dietLength,
          deliveryDates: firstDietElement.deliveryDates,
          firstDeliveryDay: firstDietElement.firstDeliveryDay,
          useEcoContainers: firstDietElement.useEcoContainers,
          addons: firstDietElement.addons,
          optionChangeMenu: firstDietElement.optionChangeMenu,
          saturdayInclude: firstDietElement.saturdayInclude,
          sundayInclude: firstDietElement.sundayInclude,
          mealTypes: firstDietElement.mealTypes,
          allowStepWithAdditionalMealTypes:
            firstDietElement.allowStepWithAdditionalMealTypes,
          hasDietsToSelectMenu: firstDietElement?.hasDietsToSelectMenu,
          ...overrideDiet,
        })
          // eslint-disable-next-line promise/prefer-await-to-then, no-shadow -- FIXME
          .then(response => {
            syncGtmCart(basket, response, { withAddedDiet: false });

            setFailureCount(0);

            const newResponseDiet = getNewDietFromBasketResponse(response);

            dispatch(setBasketMode(BASKET_MODES.NORMAL));

            dispatch(
              updateDietBasket({
                '@id': newResponseDiet?.['@id'],
                '@type': newResponseDiet?.['@type'],
                ...overrideDiet,
              })
            );

            if (isBasketStatic && basketStore?.testMode) {
              handleChangeTestMode({
                isEnabled: true,
                basketId: response?.id,
                ignoreStaticBasket: true,
                deliveryDates: firstDietElement.deliveryDates,
                customDietLength: firstDietElement.deliveryDates.length,
              });
            }

            dispatch(setBasketValidity(checkIsValid(response.uiRows)));

            return response;
          })
          // eslint-disable-next-line promise/prefer-await-to-then, promise/prefer-await-to-callbacks -- FIXME
          .catch(error => {
            setFailureCount(failureCount + 1);

            const isCancelledError = error && Object.hasOwn(error, 'silent');

            if (!isCancelledError) {
              basketCommonCatch({
                data: error?.response?.data,
                passedBasketQueryId: response['@id'],
                isInitializingError: true,
              });
            }
          })
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [basketCommonCatch, firstDietElement]
  );

  const handleAddAdditionalMealTypes = useCallback(
    async data => {
      const { mealTypeName, mealTypePrice, connectedToIri, ...restProps } =
        data ?? {};
      let basketIri;
      let connectedTo = connectedToIri ?? basketActiveOrderDietIri;

      if (isBasketStatic) {
        const response = await createBasketFromStaticBasket();
        const newDietInBasket = getNewDietFromBasketResponse(response);

        basketIri = response?.['@id'];
        connectedTo = newDietInBasket?.['@id'];
      }

      const previousAdditionalMealTypes =
        currentDiet?.connectedElements?.additionalMealTypes;

      dispatch(
        updateAdditionalMealTypes({
          item: {
            mealTypeName,
            mealTypePrice,
            dates: restProps?.days,
            quantity: restProps?.quantity,
            mealTypeIri: restProps?.mealType,
          },
          options: {
            isOnlyShop,
            connectedTo,
            basketKey,
          },
          shouldForceRange: restProps.shouldForceRange,
        })
      );

      return (
        mutateAdditionalMealTypes({
          ...restProps,
          basket: basketIri ?? basketQuery?.data?.['@id'],
          connectedTo,
        })
          // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
          .then(response => {
            dispatch(setBasketValidity(checkIsValid(response.uiRows)));
          })
          // eslint-disable-next-line promise/prefer-await-to-then, promise/prefer-await-to-callbacks -- FIXME
          .catch(error => {
            dispatch(
              setAdditionalMealTypes({
                data: previousAdditionalMealTypes,
                options: {
                  isOnlyShop,
                  connectedTo,
                  basketKey,
                },
              })
            );
            basketCommonCatch({ data: error?.response?.data });
          })
      );
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [
      basketKey,
      brandName,
      currentDiet,
      basketItems,
      currencyCode,
      basketQueryId,
      isBasketStatic,
      disableTracking,
      firstDietElement,
      basketCommonCatch,
      basketActiveOrderDietIri,
    ]
  );

  const handleClickShareStaticBasket = useCallback(async () => {
    const response = await createBasketFromStaticBasket();
    await shareBasket(response.id);
  }, [createBasketFromStaticBasket, shareBasket]);

  const duplicateBasketDiet = useCallback(
    async basketItemDiet => {
      const basket = getBasketQueryData();

      try {
        const response = await mutateBasketDietDuplicate({
          shouldDebounce: true,
          basketItemDiet,
        });

        syncGtmCart(basket, response, { asSeparateEvents: false });

        setFailureCount(0);

        const transformedResponse = transformResponseBasketRedux(
          response,
          basketItems
        );

        const lastAddedDiet =
          transformedResponse?.items?.dietElements[
            transformedResponse?.items?.dietElements?.length - 1
          ];

        dispatch(setStoreFromQuery(transformedResponse));
        dispatch(setActiveOrderDietIri(lastAddedDiet?.['@id']));
        dispatch(setBasketValidity(checkIsValid(response.uiRows)));
      } catch (error) {
        if (error === ERROR_TYPES.STALE_PROMISE) {
          return;
        }

        setFailureCount(failureCount + 1);

        const isCancelledError = error && Object.hasOwn(error, 'silent');

        if (!isCancelledError) {
          basketCommonCatch(error?.response?.data);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [basketCommonCatch]
  );

  const { isSubscriptionInCart, basketDeliveryType } = useMemo(
    () => ({
      isSubscriptionInCart:
        basketQuery?.data?.rows?.some(
          ({ paymentMode }) =>
            paymentMode === BASKET_PAYMENT_MODES.SUBSCRIPTION_PAYMENT
        ) ?? false,
      basketDeliveryType: basketQuery?.data?.defaultPickUpPoint
        ? 'pickUpPoint'
        : 'address',
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [basketQuery.dataUpdatedAt, basketQuery.errorUpdatedAt]
  );

  const testMode = useMemo(() => {
    if (isBasketStatic) {
      return basketStore.testMode;
    }
    return basketQuery?.data?.testMode ?? false;
  }, [basketStore.testMode, basketQuery?.data?.testMode, isBasketStatic]);

  const value = {
    ...basketActions,
    ...stateBasket,
    basketCommonCatch,
    basketDeliveryType,
    basketKey,
    basketQuery,
    basketStore,
    checkBasketIsValidToPayment,
    checkIsAddonSelected,
    checkIsProductSelected,
    commonSetDiscountCode,
    currentDiet,
    handleAddBasketDiet,
    handleBasketClear,
    handleChangeAddonQuantity,
    pendingAddonKey,
    addBasketItemAddonOnSinglePaymentDietMode,
    handleChangeProductQuantity,
    handleChangeTestMode,
    handleClickClearStaticBasket,
    handleClickShareStaticBasket,
    handleNotifications,
    handleRemoveBasketDiet,
    handleUpdateBasketDiet,
    handleUpdateBasketModifyDay,
    initBasket,
    isBasketEditOrder,
    isBasketNewOrder,
    isBasketStatic,
    isEmptyBasket,
    isFetchingBasket,
    isMutatingBasket,
    isSubscriptionInCart,
    recheckDiscountCode,
    setShowAbandonedModal,
    setTransformBasketStatus,
    shareBasket,
    showAbandonedModal,
    testMode,
    duplicateBasketDiet,
    transformBasketStatus,
    updateBasketModifyQuery,
    mutateDietQuery,
    updateOrderBasketModify,
    modifyBasket,
    validateDiscountCodeQuery,
    shouldScrollIntoVariants,
    setShouldScrollIntoVariants,
    handleAddAdditionalMealTypes,
    handleChangeMealTypesQuantity,
    createBasketFromStaticBasket,
  };

  return (
    <BasketMethodsContext.Provider
      value={value}
      data-cy="BasketMethodsProvider"
    >
      {children}
    </BasketMethodsContext.Provider>
  );
};

export default BasketMethodsProvider;
